import useActions from '@/composables/useActions';
import useContent from '@/composables/useContent';
import types from '@/actions/types';

export default class Sequence {
  constructor(items, id) {
    this.id = id;
    this.items = items;
    this.willComplete = false;
    this.completed = false;
    this.fork = null;
    this.paused = false;
    this.pauseResolve = null;
  }

  async runFork() {
    const startForkId = this.fork.id;
    await this.fork.run(); // eslint-disable-line
    if (startForkId === this.fork.id) {
      this.fork = null;
    } else {
      await this.runFork();
    }
  }

  async run() {
    const { setAction, currentAction } = useActions();

    // eslint-disable-next-line no-restricted-syntax
    for (const [i, item] of this.items.entries()) {
      if (this.paused) {
        await this.waitForResume(); // eslint-disable-line
      }

      if (this.fork) {
        await this.runFork(); // eslint-disable-line
      }

      if (this.completed) {
        break;
      }

      setAction(item, {
        sequenceId: this.id,
        sequenceIndex: i,
      });

      // we need to set completed before executing last action
      // since the last action should point to new Sequence and update currentSeq in useSequence
      if (i === this.items.length - 1) {
        this.willComplete = true;
      }

      // we shouldn't await for last action completion in case this is random_flow action
      if (this.willComplete && currentAction.value.name === types.RANDOM_FLOW) {
        currentAction.value.execute();
      } else {
        await currentAction.value.execute(); // eslint-disable-line
      }
    }
    this.completed = true;
  }

  waitForResume() {
    return new Promise((resolve) => {
      this.pauseResolve = resolve;
    });
  }

  pause(value) {
    this.paused = value;
    if (!this.paused && this.pauseResolve) {
      this.pauseResolve();
      this.pauseResolve = null;
    }
  }

  stop(reset = true) {
    if (this.fork) {
      this.fork.stop(reset);
    }
    const { currentAction } = useActions();
    currentAction.value.stop();

    const { resetCurrentContent, contentPromiseResolve } = useContent();

    if (contentPromiseResolve.value) {
      contentPromiseResolve.value();
    }

    if (reset) {
      resetCurrentContent();
    }
    this.completed = true;
  }
}
